import React from 'react';
import {TabsContext, TabsContextProps} from './TabsContext';
import {useId} from '../hooks';
import {useControlled} from '@mui/material';

export interface TabsProps {
    /**
     * The content of the component
     */
    children: React.ReactNode;
    /**
     * ID for Tabs wrapper
     */
    id?: string;
    /**
     * Size of Tabs
     */
    size?: 'small' | 'medium' | 'large';
    /**
     * Default index of the
     * selected tab (while uncontrolled)
     */
    defaultIndex?: number;
    /**
     * Fuel specific variations
     */
    variant?: 'container' | 'container-outlined';
    /**
     * Index of tab (while controlled)
     */
    index?: number;
    /**
     * Callback when the index (while controlled) changes.
     */
    onChange?: (index: number) => void;
}

export function makeTabId(id?: string, index?: number): string {
    return `${id}--tab-${index}`;
}

export function makeTabPanelId(id?: string, index?: number): string {
    return `${id}--tabpanel-${index}`;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(({
    children, defaultIndex, id, size = 'medium', variant, index, onChange, ...props
}, ref) => {
    const [value, setValue] = useControlled({
        controlled: index,
        default: defaultIndex ?? 0,
        name: 'Tabs'
    });

    const tabsId = useId(id, 'tabs');

    const handleChange = (event: React.SyntheticEvent, newValue: number): void => {
        onChange?.(newValue);
        setValue(newValue);
    };

    const contextValues: TabsContextProps = {
        value,
        variant,
        tabsId,
        size,
        handleChange
    };

    return (
        <TabsContext.Provider value={contextValues}>
            <div
                id={id}
                ref={ref}
                {...props}
            >
                {children}
            </div>
        </TabsContext.Provider>
    );
});

Tabs.displayName = 'Tabs';
